export default {
  methods: {
    // -----------------------------
    // Utility: Build option map
    // -----------------------------
    build_option_map(choices = []) {
      return choices.reduce((map, c) => {
        map[c.choice.toLowerCase()] = c.id;
        return map;
      }, {});
    },

    // -----------------------------
    // Helpers for dependent question
    // -----------------------------
    _get_dep_question_data(dq, all_questions_map) {
      return all_questions_map[dq.dependant_section_question_id];
    },

    _get_dep_value(dep_question_data, dep_option_map) {
      if (!dep_question_data) return null;
      return this.deserialize_answer(
        dep_question_data.answer,
        dep_question_data?.question_type?.question_type || 'short_text',
        dep_option_map
      );
    },

    _build_options(dq, dependantType) {
      const isSelectable = ['multi_select', 'single_select'].includes(dependantType);
      return isSelectable && dq.multi_select_choices
        ? dq.multi_select_choices.map(c => ({ text: c.choice, value: c.id }))
        : [];
    },

    // -----------------------------
    // Main: Build dependent question
    // -----------------------------
    build_dependent_question(dq, all_questions_map) {
        const dep_question_data = this._get_dep_question_data(dq, all_questions_map);
        const dep_option_map = dep_question_data
            ? this.build_option_map(dep_question_data.multi_select_choices)
            : {};

        const dep_answer = dep_question_data?.answer;
        const dep_value = this._get_dep_value(dep_question_data, dep_option_map);
        const dependantType = dq.dependant_question_type?.question_type || 'short_text';
        const options = this._build_options(dq, dependantType);
        const result = {
            id: dq.dependant_question_id,
            section_question_id: dq.dependant_section_question_id,
            label: dq.dependant_question_text,
            model: `dq_${dq.dependant_question_id}`,
            type: this.map_question_type(dependantType),
            required: dq.is_mandatory,
            trigger_condition: dq.trigger_condition,
            options,
            value: dep_value,
            answer: dep_answer,
            is_visible: false,
            is_dependent: true
        };
        return result;
    },

    // -----------------------------
    // Answer deserialization
    // -----------------------------
    deserialize_answer(answer, question_type, option_map) {
      if (!answer) {
        return (question_type === 'multi_select' || question_type === 'checkboxes') ? [] : '';
      }
      const type_key = (question_type || '').toLowerCase();
      const deserializer = this._get_deserializer(type_key);
      return deserializer(answer, option_map);
    },

    _get_deserializer(type_key) {
      const deserializers = {
        single_select: (answer, option_map) => this._deserialize_select(answer, option_map),
        radio: (answer, option_map) => this._deserialize_select(answer, option_map),
        multi_select: (answer, option_map) => this._deserialize_multi_select(answer, option_map),
        checkboxes: (answer, option_map) => this._deserialize_multi_select(answer, option_map),
        file: (answer) => this._deserialize_file(answer),
        facial_verification: (answer) => this._deserialize_file(answer),
      };
      return Reflect.get(deserializers, type_key) || ((answer) => answer);
    },

    _deserialize_select(answer, option_map) {
      const option_id = Reflect.get(option_map, answer.toLowerCase());
      return option_id ?? answer;
    },

    _deserialize_multi_select(answer, option_map) {
      return answer
        .split(',')
        .map(choice =>
          Reflect.get(option_map, choice.trim().toLowerCase()) ??
          choice.trim().toLowerCase()
        )
        .filter(Boolean);
    },

    _deserialize_file(answer) {
      if (typeof answer === 'object' && answer && Reflect.has(answer, 'file_name')) {
        return answer;
      }
      return typeof answer === 'string' ? { file_name: answer } : null;
    },

    // -----------------------------
    // Dependent question listeners
    // -----------------------------
    setup_dependent_question_listeners() {
      if (!this.safe_form_config.sections) return;

      this.safe_form_config.sections.forEach(section => {
        section.fields.forEach(field => {
          if (field.dependent_questions?.length > 0) {
            this.$watch(
              () => Reflect.get(this.safe_form_data, field.model),
              (newValue) => {
                this.update_dependent_questions_visibility(field, newValue);
              },
              { deep: true }
            );
            this.update_dependent_questions_visibility(
              field,
              Reflect.get(this.safe_form_data, field.model)
            );
          }
        });
      });
    },

    update_dependent_questions_visibility(field, currentValue) {
      if (!field.dependent_questions?.length) return;
      field.dependent_questions.forEach(dq => {
        dq.is_visible = this.does_value_match_trigger(currentValue, dq.trigger_condition, field);
      });
    },

    does_value_match_trigger(value, triggerCondition, field) {
      if (!triggerCondition) return false;

      if (Array.isArray(value)) {
        const selected_texts = value.map(val => {
          const option = field.options?.find(opt => String(opt.value) === String(val));
          return option ? option.text : val;
        });
        return selected_texts.includes(triggerCondition) ||
          selected_texts.some(text => this.normalize_value(text) === this.normalize_value(triggerCondition));
      }

      let value_to_compare = value;
      const option = field.options?.find(opt => String(opt.value) === String(value));
      if (option) value_to_compare = option.text;

      return this.normalize_value(value_to_compare) === this.normalize_value(triggerCondition);
    }
  }
};